import { SERVER_URL } from '../utils/constants';

function meetgeekSyncHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  const secret = (process.env.REACT_APP_MEETGEEK_SYNC_SECRET || '').trim();
  if (secret) headers['X-Meetgeek-Sync-Secret'] = secret;
  return headers;
}

/** Share paths: /host/, /team/, /candidate/ — distinct join roles */
export const buildShareLinks = (roomName, origin = typeof window !== 'undefined' ? window.location.origin : '') => {
  const base = (origin || '').replace(/\/$/, '');
  const encoded = encodeURIComponent(roomName);
  return {
    hostLink: `${base}/host/${encoded}`,
    teamLink: `${base}/team/${encoded}`,
    candidateLink: `${base}/candidate/${encoded}`,
    meetingLink: `${base}/host/${encoded}`,
  };
};

export const parseRoleJoinPath = (pathname = '') => {
  const match = String(pathname).match(/^\/(team|candidate|host)\/([^/?#]+)\/?$/i);
  if (!match) return null;
  const pathRole = match[1].toLowerCase();
  const role =
    pathRole === 'candidate'
      ? 'candidate'
      : pathRole === 'host'
        ? 'host'
        : 'team';
  return {
    role,
    roomName: decodeURIComponent(match[2]),
  };
};

export function readRecruiterQuery(search = typeof window !== 'undefined' ? window.location.search : '') {
  const params = new URLSearchParams(search);
  return {
    recruiterId: (params.get('recruiterId') || params.get('recruiter_id') || '').trim(),
    companyId: (params.get('companyId') || params.get('company_id') || '').trim(),
    name: (params.get('name') || '').trim(),
  };
}

export function roomNameFromMeetingLink(link) {
  const raw = String(link || '').trim();
  if (!raw) return '';
  try {
    const url = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://meet.getgeek.ai');
    const parsed = parseRoleJoinPath(url.pathname);
    if (parsed?.roomName) return parsed.roomName;
    return url.searchParams.get('room') || '';
  } catch {
    return '';
  }
}

export const fetchRecruiterHome = async ({ recruiterId, companyId, name, dateFrom, dateTo }) => {
  const params = new URLSearchParams({
    recruiterId,
    companyId,
  });
  if (name) params.set('name', name);
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  const response = await fetch(`${SERVER_URL}/api/recruiter/home?${params.toString()}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Failed to load recruiter calendar');
  return data;
};

export const scheduleRoom = async (roomData) => {
  const response = await fetch(`${SERVER_URL}/api/rooms/schedule`, {
    method: 'POST',
    headers: meetgeekSyncHeaders(),
    body: JSON.stringify(roomData),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to schedule room');
  return data;
};

export const createInstantRoom = async (roomData) => {
  const response = await fetch(`${SERVER_URL}/api/rooms/instant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(roomData),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create meeting');
  return data;
};

export const inviteByEmail = async (roomName, { role, email }) => {
  const response = await fetch(
    `${SERVER_URL}/api/rooms/${encodeURIComponent(roomName)}/email-invites`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role, email }),
    }
  );
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.message || data.error || 'Failed to invite by email');
    err.code = data.error;
    err.payload = data;
    throw err;
  }
  return data;
};

export const fetchSageContextOptions = async ({ roomName, companyId, email } = {}) => {
  const response = await fetch(`${SERVER_URL}/api/sage/context-options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roomName, companyId, email }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to load Sage context options');
  }
  return data;
};

export const setRoomSageContext = async (roomName, resumeScoreId, sageSessionId) => {
  const response = await fetch(
    `${SERVER_URL}/api/rooms/${encodeURIComponent(roomName)}/sage-context`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        resumeScoreId: resumeScoreId || '',
        sageSessionId: sageSessionId || '',
      }),
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to set sage context');
  return data;
};

export const resolveJoinLink = async (role, roomName) => {
  const r = String(role || '').toLowerCase();
  const pathRole =
    r === 'candidate' ? 'candidate' : r === 'host' ? 'host' : 'team';
  const response = await fetch(
    `${SERVER_URL}/api/join/${pathRole}/${encodeURIComponent(roomName)}`
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Invalid join link');
  return data;
};

export const ensureRoomInvites = async (roomName, extras = {}) => {
  const response = await fetch(
    `${SERVER_URL}/api/rooms/${encodeURIComponent(roomName)}/invites`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(extras),
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to load invite links');
  // Prefer same-origin links so local/dev shares stay on the current host
  return {
    ...data,
    ...buildShareLinks(roomName),
  };
};

export const getScheduledRooms = async (options = {}) => {
  const { status, upcoming } = options;
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (upcoming) params.append('upcoming', upcoming);
  const url = `${SERVER_URL}/api/rooms/scheduled${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch scheduled rooms');
  return data.rooms;
};

export const getScheduledRoom = async (roomName) => {
  const response = await fetch(`${SERVER_URL}/api/rooms/scheduled/${encodeURIComponent(roomName)}`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch scheduled room');
  return data;
};

export const updateScheduledRoom = async (roomName, roomData) => {
  const response = await fetch(`${SERVER_URL}/api/rooms/scheduled/${encodeURIComponent(roomName)}`, {
    method: 'PUT',
    headers: meetgeekSyncHeaders(),
    body: JSON.stringify(roomData),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update scheduled room');
  return data;
};

export const deleteScheduledRoom = async (
  roomName,
  hostName,
  { syncCalendar = true } = {}
) => {
  const response = await fetch(`${SERVER_URL}/api/rooms/scheduled/${encodeURIComponent(roomName)}`, {
    method: 'DELETE',
    headers: meetgeekSyncHeaders(),
    body: JSON.stringify({ hostName, syncCalendar }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to delete scheduled room');
  return data;
};

/** Stop egress + upload recording/artifacts (server-side). */
export const endMeeting = async (roomName, reason = 'manual') => {
  const response = await fetch(
    `${SERVER_URL}/api/rooms/${encodeURIComponent(roomName)}/end`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Failed to end meeting');
  return data;
};

export const validateScheduledRoomTime = (room, currentTime = new Date(), isHost = false) => {
  if (!room || !room.scheduledStartTime) {
    return { canJoin: false, reason: 'not_scheduled', message: 'Room is not a scheduled room' };
  }
  if (room.kind === 'instant') {
    return { canJoin: true, reason: 'available', message: 'Room is available' };
  }

  const startTime = new Date(room.scheduledStartTime);
  const endTime = new Date(room.scheduledEndTime);
  const now = new Date(currentTime);
  const earlyJoinMinutes = isHost ? 5 : 0;
  const effectiveStartTime = new Date(startTime.getTime() - earlyJoinMinutes * 60 * 1000);

  if (now < effectiveStartTime) {
    const timeUntilStart = effectiveStartTime - now;
    const hours = Math.floor(timeUntilStart / (1000 * 60 * 60));
    const minutes = Math.floor((timeUntilStart % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeUntilStart % (1000 * 60)) / 1000);
    let message = `Room not available yet. Starts at ${startTime.toLocaleString()}`;
    if (hours > 0) message += ` (in ${hours}h ${minutes}m)`;
    else if (minutes > 0) message += ` (in ${minutes}m ${seconds}s)`;
    else message += ` (in ${seconds}s)`;
    return {
      canJoin: false,
      reason: 'upcoming',
      message,
      scheduledStartTime: startTime,
      currentTime: now,
      timeUntilStart,
      hours,
      minutes,
      seconds,
    };
  }

  if (now > endTime) {
    return {
      canJoin: false,
      reason: 'ended',
      message: `Room has ended. It was scheduled until ${endTime.toLocaleString()}`,
      scheduledEndTime: endTime,
      currentTime: now,
    };
  }

  return {
    canJoin: true,
    reason: 'available',
    message: 'Room is available',
    scheduledStartTime: startTime,
    scheduledEndTime: endTime,
    currentTime: now,
  };
};

export const formatTimeUntil = (milliseconds) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};
